import React from 'react';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';

type DayHours = {
  isOpen: boolean;
  open24: boolean;
  openTime: string;
  closeTime: string;
};

export type WeekHours = Record<string, DayHours>;

export const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' }
];

export const defaultWeekHours = (): WeekHours =>
  DAYS.reduce((hours, day) => {
    hours[day.key] = {
      isOpen: true,
      open24: false,
      openTime: '09:00',
      closeTime: '17:00'
    };
    return hours;
  }, {} as WeekHours);

export const cloneWeekHours = (hours: WeekHours): WeekHours =>
  Object.fromEntries(
    Object.entries(hours).map(([day, schedule]) => [day, { ...schedule }])
  );

interface BusinessHoursSelectProps {
  value: WeekHours;
  onChange: (value: WeekHours) => void;
  allow24?: boolean;
}

export function BusinessHoursSelect({
  value,
  onChange,
  allow24 = false
}: BusinessHoursSelectProps) {
  const updateDay = (key: string, update: Partial<DayHours>) => {
    onChange({
      ...value,
      [key]: { ...value[key], ...update }
    });
  };

  return (
    <div className="space-y-3">
      {DAYS.map((day) => {
        const schedule = value[day.key];

        return (
          <div
            key={day.key}
            className="rounded-xl border p-3 sm:flex sm:items-center sm:gap-4">
            <label className="flex min-w-32 items-center gap-2 text-sm font-medium">
              <Checkbox
                checked={schedule.isOpen}
                onCheckedChange={(checked) =>
                  updateDay(day.key, { isOpen: Boolean(checked) })
                }
              />
              {day.label}
            </label>

            {schedule.isOpen && (
              <div className="mt-3 flex flex-1 flex-wrap items-center gap-3 sm:mt-0">
                {allow24 && (
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={schedule.open24}
                      onCheckedChange={(checked) =>
                        updateDay(day.key, { open24: Boolean(checked) })
                      }
                    />
                    Open 24 hours
                  </label>
                )}
                {!schedule.open24 && (
                  <div className="grid flex-1 grid-cols-2 gap-2">
                    <Input
                      type="time"
                      aria-label={`${day.label} opening time`}
                      value={schedule.openTime}
                      onChange={(event) =>
                        updateDay(day.key, { openTime: event.target.value })
                      }
                    />
                    <Input
                      type="time"
                      aria-label={`${day.label} closing time`}
                      value={schedule.closeTime}
                      onChange={(event) =>
                        updateDay(day.key, { closeTime: event.target.value })
                      }
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
